import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { authStatusChecker as AuthStatusChecker, getApiBaseUrl } from '@treneo/infrastructure/api';
import { logger } from '@treneo/shared/logger';

let listeners: (() => void)[] = [];
let connectionHealthInterval: number | null = null;
let retryTimeoutId: number | null = null;
let isConnectionInitialized = false;
let connectionRetryAttempts = 0;
let isGlobalListenersAttached = false;
let visibilityChangeHandler: (() => void) | null = null;
let refreshTokenHandler: (() => Promise<void>) | null = null;
const MAX_RETRY_ATTEMPTS = 5;

const getLogLevel = (): LogLevel => {
  if (import.meta.env.DEV) {
    return LogLevel.Debug;
  }

  return LogLevel.Warning;
};

const getSignalRUrl = (): string => {
  if (import.meta.env.DEV) {
    return `${getApiBaseUrl()}/api/coursesHub`;
  }

  return `${window.location.origin}/api/coursesHub`;
};

export const signalRConnection = new HubConnectionBuilder()
  .withUrl(getSignalRUrl(), {
    timeout: 300000,
    skipNegotiation: false
  })
  .configureLogging(getLogLevel())
  .withAutomaticReconnect({
    nextRetryDelayInMilliseconds: retryContext => {
      const isAuthError =
        retryContext.retryReason?.message?.includes('401') ||
        retryContext.retryReason?.message?.includes('Unauthorized');

      if (isAuthError) {
        logger.warn('SignalR reconnect stopped: 401 auth error. Will refresh token via onclose.');
        return null;
      }

      if (retryContext.previousRetryCount === 0) {
        return 1000;
      } else if (retryContext.previousRetryCount === 1) {
        return 5000;
      } else if (retryContext.previousRetryCount === 2) {
        return 15000;
      } else if (retryContext.previousRetryCount === 3) {
        return 30000;
      } else {
        return 60000;
      }
    }
  })
  .build();

const handleSessionExpired = async () => {
  logger.warn('SignalR session expired, attempting token refresh before reconnect');

  if (connectionHealthInterval) {
    window.clearInterval(connectionHealthInterval);
    connectionHealthInterval = null;
  }

  try {
    await signalRConnection.stop();
    emitStatusChange();
    signalRConnection.off('session_expired');

    if (refreshTokenHandler) {
      try {
        await refreshTokenHandler();
        logger.log('Token refreshed successfully, reconnecting SignalR...');

        const isAuthenticated = await AuthStatusChecker.isAuthenticated();
        if (isAuthenticated) {
          await connectSignalR();
        } else {
          logger.log('User not authenticated after refresh, triggering logout...');
          await handleLogout();
        }
      } catch (refreshError) {
        logger.warn('Token refresh failed during session expiry:', refreshError);

        const isAuthenticated = await AuthStatusChecker.isAuthenticated();
        if (isAuthenticated) {
          logger.log('User still authenticated, reconnecting SignalR...');
          await connectSignalR();
        } else {
          logger.log('User not authenticated, triggering logout...');
          await handleLogout();
        }
      }
    } else {
      logger.warn('No refresh token handler set, reconnecting SignalR without refresh...');
      await connectSignalR();
    }
  } catch (error) {
    logger.error('Error handling session expiry:', error);
    await handleLogout();
  }
};

const handleLogout = async () => {
  try {
    window.dispatchEvent(new CustomEvent('signalr:logout'));
  } catch (error) {
    logger.error('Error during logout:', error);
  }
};

const startConnectionHealthMonitoring = () => {
  if (connectionHealthInterval) {
    window.clearInterval(connectionHealthInterval);
  }

  connectionHealthInterval = window.setInterval(async () => {
    if (signalRConnection.state === HubConnectionState.Disconnected) {
      const isAuthenticated = await checkAuthentication();
      if (isAuthenticated) {
        logger.warn('SignalR connection lost, attempting to reconnect...');
        connectSignalR();
      } else {
        logger.log('User not authenticated, stopping SignalR health monitoring');
        if (connectionHealthInterval) {
          window.clearInterval(connectionHealthInterval);
          connectionHealthInterval = null;
        }
      }
    }
  }, 30000);
};

const checkAuthentication = async (): Promise<boolean> => {
  try {
    return AuthStatusChecker.isAuthenticated();
  } catch (error) {
    logger.error('Failed to check authentication status:', error);
    return false;
  }
};

export interface ConnectSignalROptions {
  skipAuthCheck?: boolean;
}

export const connectSignalR = async (options?: ConnectSignalROptions) => {
  if (signalRConnection.state === HubConnectionState.Connecting) {
    logger.log('SignalR connection already in progress');
    return;
  }

  if (signalRConnection.state === HubConnectionState.Connected) {
    logger.log('SignalR already connected');
    return;
  }

  const skipAuthCheck = options?.skipAuthCheck === true;
  if (!skipAuthCheck) {
    const isAuthenticated = await checkAuthentication();
    if (!isAuthenticated) {
      logger.log('User not authenticated, skipping SignalR connection');
      return;
    }
  }

  if (!skipAuthCheck) {
    const isTokenExpiringSoon = await AuthStatusChecker.isTokenExpiringSoon(2);
    if (isTokenExpiringSoon && refreshTokenHandler) {
      try {
        await refreshTokenHandler();
        logger.log('Token refreshed before SignalR connection');
      } catch (error) {
        logger.warn('Token refresh failed before SignalR connection:', error);
      }
    }
  }

  if (connectionRetryAttempts >= MAX_RETRY_ATTEMPTS) {
    logger.error(`SignalR connection failed after ${MAX_RETRY_ATTEMPTS} attempts, giving up`);
    return;
  }

  try {
    logger.log(`SignalR connection attempt ${connectionRetryAttempts + 1}/${MAX_RETRY_ATTEMPTS}`);

    await signalRConnection.start();

    logger.log('SignalR connected successfully');
    connectionRetryAttempts = 0;
    isConnectionInitialized = true;

    attachGlobalListeners();

    emitStatusChange();

    signalRConnection.on('session_expired', handleSessionExpired);

    signalRConnection.onreconnecting(() => {
      logger.log('SignalR reconnecting...');
      emitStatusChange();
    });

    signalRConnection.onreconnected(() => {
      logger.log('SignalR reconnected successfully');
      emitStatusChange();
    });

    signalRConnection.onclose(async error => {
      emitStatusChange();

      if (error) {
        const isAuthError =
          error.message?.includes('401') || error.message?.includes('Unauthorized');

        if (isAuthError) {
          logger.warn('SignalR closed due to auth error, triggering session expiry handler');
          await handleSessionExpired();
          return;
        }

        logger.warn('SignalR connection closed with error:', {
          error: error.message || error,
          errorType: error.name || 'UnknownError',
          connectionState: signalRConnection.state,
          timestamp: new Date().toISOString(),
          retryAttempts: connectionRetryAttempts
        });

        if (
          !error.message?.includes('Connection closed by the server') &&
          !error.message?.includes('WebSocket closed with status code: 1000')
        ) {
          logger.error('SignalR unexpected disconnection:', error);
        }
      } else {
        logger.log('SignalR connection closed gracefully');
      }
    });

    startConnectionHealthMonitoring();
  } catch (error) {
    const isAuthError =
      error instanceof Error &&
      (error.message.includes('401') || error.message.includes('Unauthorized'));

    if (isAuthError && refreshTokenHandler) {
      logger.warn('SignalR connect returned 401, refreshing token before retry...');
      try {
        await refreshTokenHandler();
        logger.log('Token refreshed, retrying SignalR connection');
        retryTimeoutId = window.setTimeout(() => {
          retryTimeoutId = null;
          connectSignalR();
        }, 1000);
      } catch (refreshError) {
        logger.warn('Token refresh failed after SignalR 401:', refreshError);
        await handleLogout();
      }
      return;
    }

    connectionRetryAttempts++;
    logger.error(`SignalR connection failed (attempt ${connectionRetryAttempts}):`, error);

    if (connectionRetryAttempts < MAX_RETRY_ATTEMPTS) {
      const retryDelay = Math.min(5000 * connectionRetryAttempts, 30000);
      retryTimeoutId = window.setTimeout(() => {
        retryTimeoutId = null;
        connectSignalR();
      }, retryDelay);
    }
  }
};

export const disconnectSignalR = () => {
  logger.log('Disconnecting SignalR...');

  if (connectionHealthInterval) {
    window.clearInterval(connectionHealthInterval);
    connectionHealthInterval = null;
  }

  if (retryTimeoutId) {
    window.clearTimeout(retryTimeoutId);
    retryTimeoutId = null;
  }

  signalRConnection.off('session_expired');
  signalRConnection.off('reconnecting');
  signalRConnection.off('reconnected');
  signalRConnection.off('close');

  if (signalRConnection.state !== HubConnectionState.Disconnected) {
    signalRConnection.stop().then(emitStatusChange).catch(logger.error);
  } else {
    emitStatusChange();
  }

  isConnectionInitialized = false;
  connectionRetryAttempts = 0;

  detachGlobalListeners();
};

export const subscribeStateChange = (listener: () => void): (() => void) => {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter(l => l !== listener);
  };
};

export const getStateSnapshot = (): typeof signalRConnection.state => signalRConnection.state;

export const isSignalRInitialized = (): boolean => isConnectionInitialized;

const attachGlobalListeners = () => {
  if (isGlobalListenersAttached || typeof window === 'undefined') {
    return;
  }

  window.addEventListener('beforeunload', disconnectSignalR);

  visibilityChangeHandler = () => {
    if (document.hidden) {
      if (connectionHealthInterval) {
        window.clearInterval(connectionHealthInterval);
        connectionHealthInterval = null;
      }
    } else {
      if (signalRConnection.state === HubConnectionState.Connected) {
        startConnectionHealthMonitoring();
      }
    }
  };
  document.addEventListener('visibilitychange', visibilityChangeHandler);

  isGlobalListenersAttached = true;
};

const detachGlobalListeners = () => {
  if (!isGlobalListenersAttached || typeof window === 'undefined') {
    return;
  }

  window.removeEventListener('beforeunload', disconnectSignalR);
  if (visibilityChangeHandler) {
    document.removeEventListener('visibilitychange', visibilityChangeHandler);
    visibilityChangeHandler = null;
  }

  isGlobalListenersAttached = false;
};

const emitStatusChange = () => {
  for (const listener of listeners) {
    listener();
  }
};

export const setRefreshTokenHandler = (handler: (() => Promise<void>) | null): void => {
  refreshTokenHandler = handler;
};
